import { useEffect, useRef, useState } from 'react';

import { Box, Link, Stack, Typography } from '@mui/material';

import { ACCENT } from '@/designs-dev/loft/accents';
import CopyForClaude from '@/designs-dev/loft/CopyForClaude';
import { baseName, type Inspection } from '@/designs-dev/inspect/fiber';
import { isLocalComponent, localFileFor } from '@/designs-dev/inspect/local';
import llamaComponents from '@/designs-dev/inspect/llama-components.json';

// Every source file drill mode can point at, loaded as text only when one is
// picked. The prototypes' own files, and llama's.
const LOCAL = import.meta.glob('/src/**/*.{ts,tsx}', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>;
const LLAMA = import.meta.glob('../../../../../lib/llama/src/**/*.{ts,tsx}', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>;

const DESIGNS = 'front-end/designs/src/';

// Whose a component is, by its name in the code. The design system is llama's
// and what has been promoted to components/ to be reused; a feature component
// is one of ours kept inside the product area that uses it; anything else is
// stock MUI. By name, because that is all the code says: a raw MUI Card and our
// wrapped Card share one, and read as ours.
const LLAMA_NAMES = new Set<string>(llamaComponents.names);
const SHARED_DIR = 'front-end/designs/src/components/';
const KINDS = {
  mui: { label: 'MUI', color: ACCENT.marine },
  system: { label: 'Design system', color: ACCENT.cherry },
  feature: { label: 'Feature', color: ACCENT.warmOrange },
} as const;
const kindOf = (name: string): keyof typeof KINDS => {
  if (LLAMA_NAMES.has(name)) return 'system';
  if (localFileFor(name)?.startsWith(SHARED_DIR)) return 'system';
  if (isLocalComponent(name)) return 'feature';
  return 'mui';
};

/**
 * A line of code with each component's name coloured by whose it is. Only
 * capitalised tags are components; a lowercase one is a plain element.
 */
const paint = (line: string) =>
  line.split(/(<\/?[A-Z]\w*)/).map((part, i) => {
    const m = part.match(/^(<\/?)([A-Z]\w*)$/);
    if (!m) return part;
    return (
      <Box component='span' key={i}>
        {m[1]}
        <Box
          component='span'
          sx={{ color: KINDS[kindOf(m[2])].color, fontWeight: 600 }}
        >
          {m[2]}
        </Box>
      </Box>
    );
  });

/**
 * A prop as it would be written at the call site. A value drill mode cannot
 * see (a function, an object, a list of elements) is a comment saying what
 * goes there, so it reads as a gap rather than as code.
 */
const attr = ({ name, value, kind }: Inspection['props'][number]) => {
  if (kind === 'function') return `${name}={/* function */}`;
  if (value === '{…}') return `${name}={/* object */}`;
  if (kind === 'element') {
    return value.startsWith('<')
      ? `${name}={${value.replace('>', ' />')}}`
      : `${name}={/* ${value} */}`;
  }
  if (value === 'true') return name;
  if (/^(-?\d+(\.\d+)?|false|null)$/.test(value)) return `${name}={${value}}`;
  return `${name}='${value}'`;
};

/** What it is made of, as children: "Typography(h5) ×2" and the like. */
const child = (part: string) => {
  const m = part.match(/^(\w+)(?:\((\w+)\))?(?: ×(\d+))?$/);
  if (!m) return `{/* ${part} */}`;
  const [, tag, variant, times] = m;
  const v = variant ? ` variant='${variant}'` : '';
  const el = `<${tag}${v}>{/* content */}</${tag}>`;
  return times ? `${el} {/* ×${times} */}` : el;
};

/**
 * The selected instance written back out as JSX: its props, its sx and style
 * if it carries any, and what it is made of as its children. Rebuilt from what
 * drill mode read off it, not copied from the source line, so what it cannot
 * see is a comment naming what goes there.
 */
const asJsx = (c: Inspection) => {
  const name = baseName(c.name);
  const attrs = c.props.map(attr);
  if (c.hasSx) attrs.push(`sx={{ ${c.sxText} }}`);
  if (c.hasStyle) attrs.push(`style={{ ${c.styleText} }}`);
  const oneLine = `<${name}${attrs.map((a) => ` ${a}`).join('')}`;
  const open =
    oneLine.length <= 60
      ? oneLine
      : `<${name}\n${attrs.map((a) => `  ${a}`).join('\n')}\n`;
  const inner = c.madeOf.length
    ? c.madeOf.map((m) => `  ${child(m)}`)
    : ['  {/* content */}'];
  return `${open}>\n${inner.join('\n')}\n</${name}>`;
};

/** Every file of ours named after a component, as repo-relative paths. */
const filesNamed = (name: string) =>
  Object.keys(LOCAL)
    .filter((k) => k.endsWith(`/${name}.tsx`))
    .map((k) => `${DESIGNS}${k.slice('/src/'.length)}`);

/** The loader for a repo-relative path, as the registry and the scan write it. */
const loaderFor = (file: string) =>
  file.startsWith(DESIGNS)
    ? LOCAL[`/src/${file.slice(DESIGNS.length)}`]
    : file.startsWith('lib/llama/')
      ? LLAMA[`../../../../../${file}`]
      : undefined;

/**
 * The selected component's source, in place of the panel's notes: the file it
 * is defined in, scrolled to where it is defined. A trial, beside the details
 * view rather than instead of it.
 *
 * It is the component's own file, not the screen that uses it, so a layout
 * Stack or a stock MUI component has nothing to show here.
 */
/**
 * The markup a component is built from, from its definition at `at`: the JSX
 * it returns, and any piece of JSX it names first (`const avatar = (`). No
 * imports, no other logic, no comments. Each line keeps its number.
 */
const markupOf = (lines: string[], at: number) => {
  if (at < 0) return [];
  // A block opened by the `(` ending line `from`, to the `)` that closes it.
  const closeOf = (from: number) => {
    let depth = 1;
    for (let i = from + 1; i < lines.length; i++) {
      for (const ch of lines[i]) {
        if (ch === '(') depth++;
        if (ch === ')') depth--;
      }
      if (depth <= 0) return i;
    }
    return lines.length - 1;
  };
  // The component's markup: the signature ending `=> (`, or its own
  // `return (`, two spaces in. Pieces it names on the way are kept too.
  const blocks: [number, number][] = [];
  for (let i = at; i < lines.length; i++) {
    const t = lines[i].trim();
    if (/=> \($/.test(lines[i]) && /^(const|\}\)|\}:)/.test(t)) {
      blocks.push([i + 1, closeOf(i) - 1]);
      break;
    }
    if (/^ {2}const \w+ = \($/.test(lines[i])) {
      const end = closeOf(i);
      blocks.push([i, end]);
      i = end;
      continue;
    }
    if (/^ {2}return \($/.test(lines[i])) {
      blocks.push([i, closeOf(i)]);
      break;
    }
  }
  // Without comments, line or block, JSX or not, and a gap between blocks.
  const out: { n: number; text: string }[] = [];
  for (const [from, to] of blocks) {
    if (out.length) out.push({ n: -from, text: '' });
    let inComment = false;
    for (let i = from; i <= to; i++) {
      const t = lines[i].trim();
      if (inComment) {
        if (t.includes('*/')) inComment = false;
        continue;
      }
      if (t.startsWith('//')) continue;
      if (t.startsWith('{/*') || t.startsWith('/*')) {
        if (!t.includes('*/')) inComment = true;
        continue;
      }
      out.push({ n: i + 1, text: lines[i] });
    }
  }
  // Moved left as far as it goes.
  const indent = Math.min(
    ...out.filter((l) => l.text.trim()).map((l) => l.text.search(/\S/)),
  );
  return out.map((l) => ({ ...l, text: l.text.slice(indent) }));
};

const CodeView = ({
  current,
  file: known,
  markupOnly = false,
}: {
  current: Inspection | null;
  /** Where it lives, from the scan or the registry. */
  file?: string;
  /**
   * Only the markup it returns, for reading how it is built: none of the
   * imports, notes or logic around it. Drill mode shows the whole file.
   */
  markupOnly?: boolean;
}) => {
  const [source, setSource] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const defined = useRef<HTMLDivElement>(null);
  const name = current ? baseName(current.name) : '';

  // One of ours whose name is in more than one file (two Flows): the scan
  // cannot say which this is, so the files of that name are offered to pick
  // from. Forgotten when the selection changes.
  const shared = Boolean(name) && isLocalComponent(name) && !known;
  const candidates = shared ? filesNamed(name) : [];
  const [picked, setPicked] = useState<string>();
  useEffect(() => setPicked(undefined), [name]);
  const file = known ?? picked;
  const load = file ? loaderFor(file) : undefined;

  useEffect(() => {
    setSource(null);
    if (!load) return;
    let live = true;
    load().then((text) => live && setSource(text));
    return () => {
      live = false;
    };
  }, [load]);

  const lines = source?.split('\n') ?? [];
  const at = lines.findIndex((l) =>
    new RegExp(`(const|function|class) ${name}\\b`).test(l),
  );

  // The code box scrolled, not the line: scrollIntoView would move the panel
  // and the page along with it.
  useEffect(() => {
    const box = scroller.current;
    if (box) box.scrollTop = Math.max(0, (defined.current?.offsetTop ?? 0) - 8);
  }, [source, at]);

  if (!current) return null;

  const code = {
    m: 0,
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.6,
  } as const;

  // Each box is as tall as its code, and shrinks to share the panel's height
  // once the code is taller than that, scrolling inside itself.
  return (
    <Stack spacing={3} sx={{ minHeight: 0, flex: '0 1 auto' }}>
      {/* This one, as it is written where it is used. For stock MUI, which
          has no file of ours, this is the code. */}
      {/* Never shrinks: sharing the height with the definition squeezed it to a
          single clipped line whenever the file below was long. Capped instead,
          and scrolls inside itself past that. */}
      <Stack spacing={1} sx={{ flexShrink: 0 }}>
        <Typography variant='subtitle2'>As used here</Typography>
        <Box
          component='pre'
          sx={{
            ...code,
            p: 1.5,
            overflow: 'auto',
            maxHeight: '40vh',
            border: 1,
            borderColor: 'custom.neutral.200',
            borderRadius: 1,
            bgcolor: 'background.default',
          }}
        >
          {asJsx(current)
            .split('\n')
            .map((line, i) => (
              <Box key={i}>{paint(line)}</Box>
            ))}
        </Box>
        <Stack direction='row' spacing={2}>
          {Object.values(KINDS).map((k) => (
            <Stack
              key={k.label}
              direction='row'
              spacing={0.75}
              sx={{ alignItems: 'center' }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: k.color,
                }}
              />
              <Typography variant='caption' color='text.secondary'>
                {k.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* More than one file by this name: which is it? Stays up once one is
          picked, so another can be. */}
      {shared && (
        <Stack spacing={1} sx={{ flexShrink: 0 }}>
          <Typography variant='body2' color='text.secondary'>
            {candidates.length > 1
              ? `There are ${candidates.length} components called ${name}, so drill mode can't tell which this is. Pick one to see it:`
              : `There's more than one component called ${name}, so drill mode can't tell which this is.`}
          </Typography>
          {candidates.length > 1 &&
            candidates.map((c) => (
              <Link
                key={c}
                component='button'
                variant='mono'
                underline='hover'
                onClick={() => setPicked(c)}
                sx={{
                  textAlign: 'left',
                  wordBreak: 'break-all',
                  fontWeight: c === picked ? 600 : undefined,
                }}
              >
                {c}
              </Link>
            ))}
        </Stack>
      )}

      {!file || !load ? (
        shared ? null : (
          <Typography variant='body2' color='text.secondary'>
            {current.isLayout
              ? 'Written inline in the screen that uses it, so it has no file of its own.'
              : file
                ? `${file} is outside what drill mode can open.`
                : "Stock MUI, styled by the theme. There's no file of ours behind it."}
          </Typography>
        )
      ) : (
        <Stack spacing={1} sx={{ minHeight: 0, flex: '0 1 auto' }}>
          <Typography variant='subtitle2'>Where it is defined</Typography>
          {/* The path is the code block's own header, the way an editor tab
              names its file, with the copy beside it. As a loose line above the
              box it wrapped across two lines and read as a third block. */}
          <Box
            sx={{
              flex: '0 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              border: 1,
              borderColor: 'custom.neutral.200',
              borderRadius: 1,
              bgcolor: 'background.default',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction='row'
              spacing={1}
              sx={{
                flexShrink: 0,
                alignItems: 'center',
                pl: 1.5,
                pr: 0.5,
                py: 0.25,
                borderBottom: 1,
                borderColor: 'custom.neutral.200',
              }}
            >
              <Typography
                variant='mono'
                noWrap
                title={file}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  direction: 'rtl',
                  textAlign: 'left',
                }}
              >
                {/* rtl so a long path loses its start, not the file name; the
                    path itself stays ltr inside, or its slashes flip */}
                <Box
                  component='span'
                  sx={{ direction: 'ltr', unicodeBidi: 'isolate' }}
                >
                  <Box component='span' sx={{ color: 'text.secondary' }}>
                    {file.slice(0, file.lastIndexOf('/') + 1)}
                  </Box>
                  {file.slice(file.lastIndexOf('/') + 1)}
                </Box>
              </Typography>
              <CopyForClaude text={() => file} title='Copy file path' />
            </Stack>
            <Box
              ref={scroller}
              sx={{
                flex: '0 1 auto',
                minHeight: 0,
                overflow: 'auto',
                position: 'relative',
                py: 1,
              }}
            >
              {source === null ? (
                <Typography
                  variant='mono'
                  color='text.secondary'
                  sx={{ px: 1.5 }}
                >
                  Loading…
                </Typography>
              ) : (
                <Box
                  component='pre'
                  sx={{
                    ...code,
                    width: 'max-content',
                    minWidth: '100%',
                  }}
                >
                  {(markupOnly
                    ? markupOf(lines, at)
                    : lines.map((text, i) => ({ n: i + 1, text }))
                  ).map(({ n, text: line }) => (
                    <Box
                      key={n}
                      ref={n - 1 === at ? defined : undefined}
                      sx={{
                        display: 'flex',
                        pr: 1.5,
                        bgcolor:
                          n - 1 === at && !markupOnly
                            ? 'custom.sunflower.100'
                            : undefined,
                      }}
                    >
                      <Box
                        component='span'
                        sx={{
                          width: '4ch',
                          flexShrink: 0,
                          textAlign: 'right',
                          mr: 2,
                          color: 'text.disabled',
                          userSelect: 'none',
                        }}
                      >
                        {n > 0 ? n : ''}
                      </Box>
                      <Box component='span'>{line ? paint(line) : ' '}</Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default CodeView;
